import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import CustomFormDrawer from 'components/shared/customFormDrawer';
import { api_succession_talent_pool_members } from 'constants/apiRoutes';
import { SUCCESSOR_FORM_MODE } from 'constants/succession';
import http from 'utility/http';
import { isSuccessionUserRemoved } from 'utility/succession';
import {
  prepareSuccessorFormFields,
  successorFormInitialData,
  preparePoolMembers,
} from './config';

const { ADD, EDIT, REMOVE, READD } = SUCCESSOR_FORM_MODE;

const ManageSuccessorForm = ({
  translations,
  isOpened,
  mode = ADD,
  successor = null,
  employees = [],
  pools = [],
  readinessOptions = [],
  removalReasons = [],
  onClose,
  onSave,
}) => {
  const [poolMembers, setPoolMembers] = useState([]);

  const getPoolMembers = useCallback(async poolId => {
    if (!poolId) {
      setPoolMembers([]);
      return;
    }

    const { data } = await http.get(
      api_succession_talent_pool_members(poolId),
      { params: { page_size: 500 } }
    );
    setPoolMembers(preparePoolMembers(data?.results || []));
  }, []);

  const fields = useMemo(
    () =>
      prepareSuccessorFormFields({
        labels: translations.sourceOptions,
        mode,
        successor,
        onPoolChange: getPoolMembers,
      }),
    [getPoolMembers, mode, successor, translations.sourceOptions]
  );

  const initialData = useMemo(
    () => successorFormInitialData({ mode, successor }),
    // Keep the opened values stable while the parent refetches the same record.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpened, mode, successor?.id]
  );

  const handleSave = values => {
    const employeeId =
      values?.employee?.id ||
      values?.pool_member?.id ||
      successor?.employee?.id ||
      successor?.user?.id ||
      successor?.person?.id ||
      null;
    const isCreateMode = mode === ADD || mode === READD;

    return onSave({
      mode,
      successorId: isCreateMode ? employeeId : successor?.id || employeeId,
      readinessLevelId: values.readiness_level,
      rank: values.rank,
      note: values.note || '',
      reasonId: values.reason,
      comment: values.comment || '',
    });
  };

  const isViewingRemoved =
    mode === REMOVE && isSuccessionUserRemoved(successor?.status);

  return (
    <CustomFormDrawer
      translations={translations}
      isOpened={isOpened}
      initialData={initialData}
      fields={fields}
      employees={employees}
      poolMembers={poolMembers}
      pools={pools}
      readinessOptions={readinessOptions}
      removalReasons={removalReasons}
      onClose={onClose}
      onSave={handleSave}
      getTitle={() => {
        if (mode === REMOVE) return translations.remove;
        if (mode === EDIT) return translations.edit;
        if (mode === READD) return translations.readd;
        return translations.add;
      }}
      isInitialValid={mode === EDIT}
      hasCancelButton
      hideDelete
      shouldHideSave={isViewingRemoved}
      isMiddleWidth={!isViewingRemoved}
    />
  );
};

ManageSuccessorForm.propTypes = {
  isOpened: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(Object.values(SUCCESSOR_FORM_MODE)),
  successor: PropTypes.object,
  employees: PropTypes.arrayOf(PropTypes.object),
  pools: PropTypes.arrayOf(PropTypes.object),
  readinessOptions: PropTypes.arrayOf(PropTypes.object),
  removalReasons: PropTypes.arrayOf(PropTypes.object),
  translations: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default ManageSuccessorForm;
